import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MessageSquare, Users, Activity, Star, TrendingUp, Briefcase, Truck, Clock, CheckCircle2, ClipboardList } from "lucide-react";
import ForexManagement from "@/components/ForexRatesManagement";
import PackageManagement from "@/components/PackageManagement";
import ForexDeliveryOrders from "@/components/ForexDeliveryOrders";
import { buildApiUrl } from "@/lib/config";
import Seo from "@/components/Seo";
import { organizationJsonLd, websiteJsonLd } from "@/lib/seo";

interface Contact {
  id: number;
  name: string;
  email: string;
  phone: string;
  message: string;
  createdAt: string;
}

interface JobApplication {
  id: number;
  name: string;
  email: string;
  phone: string;
  position: string;
  experience: string;
  coverLetter: string;
  resumeUrl?: string;
  createdAt: string;
}

interface ForexRate {
  id: number;
  currencyCode: string;
  rate: string;
  date: string;
  createdAt: string;
  updatedAt: string;
}

// Forex Rates Display Component
function ForexRatesDisplay() {
  const { data: forexRates = [], isLoading } = useQuery<ForexRate[]>({
    queryKey: ["/api/forex-rates/today"],
    queryFn: async () => {
      const response = await fetch(buildApiUrl('api/forex-rates/today'));
      if (!response.ok) throw new Error('Failed to fetch forex rates');
      return response.json();
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!forexRates.length) {
    return (
      <p className="text-sm text-gray-500 py-4">No forex rates available</p>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {forexRates.map((rate) => (
        <div key={rate.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-medium text-gray-900">{rate.currencyCode}</p>
              <p className="text-xs text-gray-500">vs INR</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-green-600">₹{parseFloat(rate.rate).toFixed(2)}</p>
              <p className="text-xs text-gray-500">{new Date(rate.date).toLocaleDateString()}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const normalizeRole = (role: string) =>
  (role || "").toLowerCase().replace(/_/g, " ").trim();

const formatRole = (role: string) =>
  (role || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Dashboard() {
  const { user, isLoading, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  // Fetch contacts
  const { data: contacts = [] } = useQuery<Contact[]>({
    queryKey: ["/api/admin/contacts"],
    enabled: !!user && normalizeRole(user.role) === "admin",
  });

  // Fetch job applications
  const { data: jobApplications = [] } = useQuery<JobApplication[]>({
    queryKey: ["/api/admin/job-applications"],
    enabled: !!user && normalizeRole(user.role) === "admin",
  });

  // Fetch dashboard stats
  const { data: dashboardStats } = useQuery({
    queryKey: ["/api/admin/dashboard"],
    enabled: !!user && normalizeRole(user.role) === "admin",
  });

  // Fetch forex delivery orders (Forex Manager + Admin)
  const { data: forexOrders = [] } = useQuery<any[]>({
    queryKey: ["/api/forex-delivery/orders"],
    queryFn: async () => {
      const token = localStorage.getItem("authToken");
      const res = await fetch(buildApiUrl("api/forex-delivery/orders"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch orders");
      return res.json();
    },
    enabled: !!user && (normalizeRole(user.role) === "admin" || normalizeRole(user.role) === "forex manager"),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Seo
          title="Dashboard | Travel Wings Global"
          description="Internal dashboard access."
          path="/dashboard"
          structuredData={[organizationJsonLd, websiteJsonLd]}
          noindex
        />
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Seo
          title="Dashboard | Travel Wings Global"
          description="Internal dashboard access."
          path="/dashboard"
          structuredData={[organizationJsonLd, websiteJsonLd]}
          noindex
        />
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">Please log in to access the dashboard.</p>
        </div>
      </div>
    );
  }

  const userRoleNorm = normalizeRole(user.role);
  const isAdmin = userRoleNorm === "admin";
  const canManageForex = isAdmin || userRoleNorm === "forex manager";
  const canManagePackages = isAdmin || userRoleNorm === "package manager" || userRoleNorm === "travel manager";
  const canViewDeliveryOrders = canManageForex;

  // Display helpers
  const displayName = user.firstName
    ? `${user.firstName}${user.lastName ? " " + user.lastName : ""}`
    : user.username && !user.username.includes("-") ? user.username : user.email;
  const displayRole = formatRole(user.role);

  // Role-specific analytics visibility
  const showContactsAndJobs = isAdmin;
  const showPackageAnalytics = isAdmin || canManagePackages;
  const showForexAnalytics = isAdmin || canManageForex;

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        title="Dashboard | Travel Wings Global"
        description="Internal dashboard for Travel Wings Global team."
        path="/dashboard"
        structuredData={[organizationJsonLd, websiteJsonLd]}
        noindex
      />
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <img 
                  src="/logo.png" 
                  alt="Travel Wings Logo" 
                  className="h-8 w-auto"
                />
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Travel Wings Dashboard</h1>
                  <p className="text-sm text-gray-500">Welcome back, {displayName}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Badge variant="secondary" className="text-xs px-2 py-1">
                {displayRole}
              </Badge>
              <Button onClick={handleLogout} variant="outline" size="sm" className="text-sm">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-4 sm:py-8">
        <Tabs defaultValue="overview" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <TabsList className={`grid w-full ${
            isAdmin ? 'grid-cols-2 sm:grid-cols-4' :
            canManageForex ? 'grid-cols-3' :
            canManagePackages ? 'grid-cols-1 sm:grid-cols-2' :
            'grid-cols-1'
          } gap-1 h-auto p-1 mb-6`}>
            <TabsTrigger
              value="overview"
              className="text-xs sm:text-sm px-2 py-2 h-auto data-[state=active]:bg-white data-[state=active]:text-gray-900"
            >
              Overview
            </TabsTrigger>
            {canManageForex && (
              <TabsTrigger
                value="forex"
                className="text-xs sm:text-sm px-2 py-2 h-auto data-[state=active]:bg-white data-[state=active]:text-gray-900"
              >
                Forex Management
              </TabsTrigger>
            )}
            {canManagePackages && (
              <TabsTrigger
                value="packages"
                className="text-xs sm:text-sm px-2 py-2 h-auto data-[state=active]:bg-white data-[state=active]:text-gray-900"
              >
                Package Management
              </TabsTrigger>
            )}
            {canViewDeliveryOrders && (
              <TabsTrigger
                value="delivery"
                className="text-xs sm:text-sm px-2 py-2 h-auto data-[state=active]:bg-white data-[state=active]:text-gray-900"
              >
                Forex Orders
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Analytics Cards */}
            <div className={`grid grid-cols-2 ${
              isAdmin ? 'lg:grid-cols-5' :
              canManageForex ? 'sm:grid-cols-4' :
              (showPackageAnalytics && showForexAnalytics) ? 'lg:grid-cols-3' :
              'lg:grid-cols-2'
            } gap-4 sm:gap-6 mb-6 sm:mb-8`}>
              
              {/* Total Contacts - Admin Only */}
              {showContactsAndJobs && (
                <Card className="bg-white border border-gray-200">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2">Total Contacts</p>
                        <p className="text-lg sm:text-2xl font-bold text-gray-900">
                          {Array.isArray(contacts) ? contacts.length : 0}
                        </p>
                      </div>
                      <MessageSquare className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Total Job Applications - Admin Only */}
              {showContactsAndJobs && (
                <Card className="bg-white border border-gray-200">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2">Job Applications</p>
                        <p className="text-lg sm:text-2xl font-bold text-gray-900">
                          {Array.isArray(jobApplications) ? jobApplications.length : 0}
                        </p>
                      </div>
                      <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Active Users - Admin Only */}
              {isAdmin && (
                <Card className="bg-white border border-gray-200">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2">Active Users</p>
                        <p className="text-lg sm:text-2xl font-bold text-gray-900">
                          {dashboardStats?.activeUsers || 6}
                        </p>
                      </div>
                      <Users className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Total Packages - Package Managers and Admin */}
              {showPackageAnalytics && (
                <Card className="bg-white border border-gray-200">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2">Travel Packages</p>
                        <p className="text-lg sm:text-2xl font-bold text-gray-900">
                          {dashboardStats?.totalPackages || 3}
                        </p>
                      </div>
                      <Star className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-600" />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Monthly Revenue - Admin and Forex Manager */}
              {(isAdmin || showForexAnalytics) && (
                <Card className="bg-white border border-gray-200">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2">Monthly Revenue</p>
                        <p className="text-lg sm:text-2xl font-bold text-gray-900">
                          ₹{dashboardStats?.monthlyRevenue || "2.5M"}
                        </p>
                      </div>
                      <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600" />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Total Forex Orders - Forex Manager and Admin */}
              {canManageForex && (
                <Card className="bg-white border border-gray-200">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2">Total Forex Orders</p>
                        <p className="text-lg sm:text-2xl font-bold text-gray-900">{forexOrders.length}</p>
                      </div>
                      <ClipboardList className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500" />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Pending Forex Orders - Forex Manager and Admin */}
              {canManageForex && (
                <Card className="bg-white border border-gray-200">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2">Pending Orders</p>
                        <p className="text-lg sm:text-2xl font-bold text-yellow-600">
                          {forexOrders.filter((o: any) => o.status === "Pending").length}
                        </p>
                      </div>
                      <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500" />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Completed Forex Orders - Forex Manager and Admin */}
              {canManageForex && (
                <Card className="bg-white border border-gray-200">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2">Completed Orders</p>
                        <p className="text-lg sm:text-2xl font-bold text-green-600">
                          {forexOrders.filter((o: any) => o.status === "Completed").length}
                        </p>
                      </div>
                      <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Dynamic Content Grid based on Role */}
            <div className={`grid grid-cols-1 ${
              isAdmin ? 'lg:grid-cols-3' : 'lg:grid-cols-2'
            } gap-6`}>
              
              {/* User Profile - Always Visible */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center space-x-2 text-base sm:text-lg">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>User Profile</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 sm:space-y-4 pt-0">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-semibold text-sm">
                        {displayName?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm sm:text-base font-medium text-gray-900">{displayName}</p>
                      <p className="text-xs sm:text-sm text-gray-500">{displayRole}</p>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-gray-100">
                    <div className="flex items-center space-x-2">
                      <Activity className="w-4 h-4 text-green-500" />
                      <span className="text-xs sm:text-sm text-gray-600">Active Session</span>
                    </div>
                    <div className="mt-2 text-xs text-gray-600">
                      <strong>Permissions:</strong> {
                        isAdmin ? "Full System Access" :
                        canManagePackages ? "Package Management" :
                        canManageForex ? "Forex Rate Management" :
                        "Basic Access"
                      }
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Forex Rates Display - Forex Managers and Admin */}
              {canManageForex && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base sm:text-lg">Current Exchange Rates</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <ForexRatesDisplay />
                  </CardContent>
                </Card>
              )}

              {/* Pending Forex Orders Quick View - Forex Manager and Admin */}
              {canManageForex && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                      <Truck className="w-4 h-4 text-orange-500" />
                      Recent Forex Orders
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {forexOrders.length === 0 ? (
                      <p className="text-sm text-gray-500 py-2">No orders yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {forexOrders.slice(0, 4).map((order: any) => {
                          const isPending = order.status === "Pending";
                          const isPickup = order.deliveryAddress?.startsWith("BRANCH PICKUP");
                          return (
                            <div key={order.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-xs">
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 truncate">{order.fullName}</p>
                                <p className="text-gray-500">{isPickup ? "Branch Pickup" : "Home Delivery"} · {order.currencyCode}</p>
                              </div>
                              <span className={`ml-2 shrink-0 px-2 py-0.5 rounded-full font-medium ${
                                isPending ? "bg-yellow-100 text-yellow-700" :
                                order.status === "Completed" ? "bg-green-100 text-green-700" :
                                order.status === "In Progress" ? "bg-blue-100 text-blue-700" :
                                "bg-red-100 text-red-700"
                              }`}>{order.status}</span>
                            </div>
                          );
                        })}
                        {forexOrders.length > 4 && (
                          <p className="text-xs text-gray-400 text-center pt-1">+{forexOrders.length - 4} more — see Forex Orders tab</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Recent Contacts - Admin Only */}
              {isAdmin && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base sm:text-lg">Recent Contacts</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {Array.isArray(contacts) && contacts.length > 0 ? (
                      <div className="space-y-2 sm:space-y-3">
                        {contacts.slice(0, 3).map((contact) => (
                          <div key={contact.id} className="flex items-start space-x-3 p-2 sm:p-3 bg-gray-50 rounded-lg">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <MessageSquare className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">{contact.name}</p>
                              <p className="text-xs text-gray-500 truncate">{contact.email}</p>
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">{contact.message}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No contacts available</p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Recent Job Applications - Admin Only */}
              {isAdmin && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base sm:text-lg">Recent Job Applications</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {Array.isArray(jobApplications) && jobApplications.length > 0 ? (
                      <div className="space-y-2 sm:space-y-3">
                        {jobApplications.slice(0, 3).map((application) => (
                          <div key={application.id} className="flex items-start space-x-3 p-2 sm:p-3 bg-gray-50 rounded-lg">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <Briefcase className="w-4 h-4 text-green-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">{application.name}</p>
                              <p className="text-xs text-gray-500 truncate">{application.position}</p>
                              <p className="text-xs text-gray-600">{application.experience} experience</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No job applications available</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="forex" className="space-y-6">
            <ForexManagement />
          </TabsContent>

          <TabsContent value="packages" className="space-y-6">
            <PackageManagement />
          </TabsContent>

          {canViewDeliveryOrders && (
            <TabsContent value="delivery" className="space-y-6">
              <ForexDeliveryOrders />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}